import 'src/utils/highlight';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
// @mui
import { alpha } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
//
import { EditorProps } from './types';
import { StyledEditor } from './styles';
import Toolbar, { formats } from './toolbar';

// ----------------------------------------------------------------------

export default function Editor({
  id = 'minimal-quill',
  error,
  simple = false,
  helperText,
  sx,
    showtoolbar = true,
  ...other
}: EditorProps) {
  const quillRef = useRef<ReactQuill | null>(null);
  const { t } = useTranslation();

  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [openInNewTab, setOpenInNewTab] = useState(true);
  const lastSelectionRef = useRef<{ index: number; length: number } | null>(null);

  const openLinkDialog = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection();
    lastSelectionRef.current = range || null;
    const selectedText = range && range.length > 0 ? editor.getText(range.index, range.length) : '';
    setLinkText(selectedText.trim());
    setLinkUrl('');
    setIsLinkDialogOpen(true);
  };

  const normalizeUrl = (url: string) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const applyLink = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const url = normalizeUrl(linkUrl);
    if (!url) {
      setIsLinkDialogOpen(false);
      return;
    }

    const range = lastSelectionRef.current || editor.getSelection();
    if (!range) {
      setIsLinkDialogOpen(false);
      return;
    }

    // If nothing selected, insert the provided text and then apply link
    if (range.length === 0) {
      const textToInsert = linkText || url;
      editor.insertText(range.index, textToInsert);
      editor.setSelection(range.index, textToInsert.length);
    } else if (linkText && editor.getText(range.index, range.length) !== linkText) {
      // Replace selected text if user edited it in the dialog
      editor.deleteText(range.index, range.length);
      editor.insertText(range.index, linkText);
      editor.setSelection(range.index, linkText.length);
    }

    editor.format('link', url);

    // Optionally set target _blank using Quill's built-in support via rel attribute
    if (openInNewTab) {
      const [leaf] = editor.getLeaf(editor.getSelection()?.index || 0);
      if (leaf && (leaf as any).parent && (leaf as any).parent.domNode) {
        const domNode = (leaf as any).parent.domNode as HTMLAnchorElement;
        if (domNode && domNode.tagName === 'A') {
          domNode.setAttribute('target', '_blank');
          domNode.setAttribute('rel', 'noopener noreferrer');
        }
      }
    }

    setIsLinkDialogOpen(false);
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: `#${id}`,
      handlers: {
        link: () => openLinkDialog(),
      },
    },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    syntax: true,
    clipboard: {
      matchVisual: false,
    },
  }), [id]);

  return (
    <>
      <StyledEditor
        sx={{
          ...(error && {
            border: (theme) => `solid 1px ${theme.palette.error.main}`,
            '& .ql-editor': {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            },
          }),
          ...sx,
        }}
        translate="no"
      >

        <Toolbar id={id} isSimple={simple} showtoolbar={showtoolbar} />


        <ReactQuill
          ref={quillRef}
          modules={modules}
          formats={formats}
          placeholder={t('WRITE_SOMETHING_AWESOME') || 'Write something awesome...'}
          {...other}
        />
      </StyledEditor>

      {helperText && helperText}

      <Dialog open={isLinkDialogOpen} onClose={() => setIsLinkDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('ADD_LINK') || 'Add link'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t('TEXT') || 'Text'}
            fullWidth
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t('URL') || 'URL'}
            placeholder={t('URL_PLACEHOLDER') || 'https://example.com'}
            fullWidth
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
          <FormControlLabel
            control={<Checkbox checked={openInNewTab} onChange={(e) => setOpenInNewTab(e.target.checked)} />}
            label={t('OPEN_IN_NEW_TAB') || 'Open in new tab'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsLinkDialogOpen(false)}>{t('CANCEL') || 'Cancel'}</Button>
          <Button variant="contained" onClick={applyLink}>{t('INSERT') || 'Insert'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
